import { useState, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { AppSidebar } from '@/components/app-sidebar';
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { TemplatePreviewModal } from '@/components/template-preview-modal';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  RiLayoutGridLine,
  RiSearchLine,
  RiEyeLine,
  RiFileCopyLine,
  RiLoader4Line,
  RiFilterLine,
  RiCloseLine,
  RiCheckLine,
} from '@remixicon/react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const CATEGORIES = [
  'All',
  'E-commerce',
  'SaaS',
  'Social Media',
  'Blog/CMS',
  'Analytics',
  'Auth',
  'Payments',
  'Messaging',
  'Starter',
  'Intermediate',
  'Advanced',
];

interface Template {
  template: {
    id: string;
    title: string;
    description: string | null;
    categories: string[];
    thumbnailUrl: string | null;
    viewCount: number;
    duplicateCount: number;
    publishedAt: string;
  };
  schema: {
    id: string;
    nodes: any[];
    edges: any[];
  };
  user: {
    id: string;
    name: string | null;
  };
}

export function TemplatesExplorePage() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [sortBy, setSortBy] = useState<'recent' | 'popular' | 'mostDuplicated'>('recent');
  const [total, setTotal] = useState(0);
  const [previewTemplateId, setPreviewTemplateId] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, [selectedCategory, sortBy, searchQuery]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const params: any = {
        sortBy,
        limit: 50,
        offset: 0,
      };

      if (selectedCategory !== 'All') {
        params.category = selectedCategory;
      }

      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }

      const response = await api.getPublicTemplates(params);
      setTemplates(response.templates);
      setTotal(response.total);
    } catch (error: any) {
      console.error('Failed to fetch templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicate = async (templateId: string) => {
    try {
      setDuplicatingId(templateId);
      const response = await api.duplicateTemplate(templateId);
      toast.success('Template duplicated to your workspace!');

      // Navigate to the schema designer with the new schema
      navigate({ to: '/visual-schema-designer' });
    } catch (error: any) {
      console.error('Failed to duplicate template:', error);
      toast.error(error.message || 'Failed to duplicate template');
    } finally {
      setDuplicatingId(null);
    }
  };

  const handleViewTemplate = (templateId: string) => {
    setPreviewTemplateId(templateId);
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewTemplateId(null);
  };

  const handleDuplicateFromPreview = () => {
    // Refresh the gallery after duplication
    fetchTemplates();
  };

  return (
    <SidebarProvider defaultOpen={false}>
      <AppSidebar />
      <div className="h-screen w-full flex flex-col overflow-hidden">
        {/* Header */}
        <header className="flex h-16 shrink-0 items-center gap-2 border-b px-4">
          <SidebarTrigger className="-ml-1" />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbPage className="flex items-center gap-2">
                  <RiLayoutGridLine className="h-4 w-4" />
                  Community Templates
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </header>

        {/* Main Content */}
        <div className="flex-1 overflow-hidden">
          <div className="h-full flex flex-col p-6">
            {/* Search and Filters */}
            <div className="flex-shrink-0 mb-6 space-y-4">
              {/* Search Bar */}
              <div className="flex gap-3 items-center">
                <div className="relative flex-1 max-w-md">
                  <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search templates..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>

                <Select value={sortBy} onValueChange={(value: any) => setSortBy(value)}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="recent">Most Recent</SelectItem>
                    <SelectItem value="popular">Most Popular</SelectItem>
                    <SelectItem value="mostDuplicated">Most Duplicated</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Category Filters */}
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map((category) => (
                  <Badge
                    key={category}
                    variant={selectedCategory === category ? 'default' : 'outline'}
                    className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
                    onClick={() => setSelectedCategory(category)}
                  >
                    {category}
                    {selectedCategory === category && (
                      <RiCheckLine className="ml-1 h-3 w-3" />
                    )}
                  </Badge>
                ))}
              </div>

              {/* Results count */}
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  {loading ? 'Loading...' : `${total} ${total === 1 ? 'template' : 'templates'} found`}
                </p>
                {(selectedCategory !== 'All' || searchQuery) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedCategory('All');
                      setSearchQuery('');
                    }}
                    className="gap-2"
                  >
                    <RiCloseLine className="h-4 w-4" />
                    Clear filters
                  </Button>
                )}
              </div>
            </div>

            {/* Templates Grid */}
            <div className="flex-1 overflow-y-auto pr-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <RiLoader4Line className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : templates.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-center">
                  <RiLayoutGridLine className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No templates found</h3>
                  <p className="text-sm text-muted-foreground max-w-md">
                    {searchQuery || selectedCategory !== 'All'
                      ? 'Try adjusting your filters or search query'
                      : 'Be the first to publish a template!'}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 pb-4">
                  {templates.map(({ template, schema, user }) => (
                    <Card
                      key={template.id}
                      className="group flex flex-col hover:shadow-lg transition-all duration-200 overflow-hidden border-muted hover:border-primary/20"
                    >
                      {/* Thumbnail */}
                      <div className="relative w-full h-48 bg-gradient-to-br from-muted/30 to-muted/10 overflow-hidden">
                        <img
                          src={template.thumbnailUrl || '/nothumbnail.webp'}
                          alt={template.title}
                          className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
                          onError={(e) => {
                            e.currentTarget.src = '/nothumbnail.webp';
                          }}
                        />
                        {/* Category badges overlay */}
                        <div className="absolute top-2 right-2 flex flex-wrap gap-1 max-w-[60%] justify-end">
                          {template.categories.slice(0, 2).map((category) => (
                            <Badge
                              key={category}
                              variant="secondary"
                              className="text-xs backdrop-blur-sm bg-background/80"
                            >
                              {category}
                            </Badge>
                          ))}
                          {template.categories.length > 2 && (
                            <Badge
                              variant="secondary"
                              className="text-xs backdrop-blur-sm bg-background/80"
                            >
                              +{template.categories.length - 2}
                            </Badge>
                          )}
                        </div>
                      </div>

                      <CardHeader className="pb-3">
                        <CardTitle className="text-base font-semibold line-clamp-1 group-hover:text-primary transition-colors">
                          {template.title}
                        </CardTitle>
                        <CardDescription className="line-clamp-2 text-sm">
                          {template.description || 'No description provided'}
                        </CardDescription>
                      </CardHeader>

                      <CardContent className="flex-1 pb-3">
                        {/* Stats */}
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <div className="flex items-center gap-1.5">
                            <RiLayoutGridLine className="h-3.5 w-3.5" />
                            <span>{schema.nodes.length}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <RiEyeLine className="h-3.5 w-3.5" />
                            <span>{template.viewCount}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <RiFileCopyLine className="h-3.5 w-3.5" />
                            <span>{template.duplicateCount}</span>
                          </div>
                        </div>
                      </CardContent>

                      <CardFooter className="flex gap-2 pt-3 border-t">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 gap-1.5"
                          onClick={() => handleViewTemplate(template.id)}
                        >
                          <RiEyeLine className="h-3.5 w-3.5" />
                          Preview
                        </Button>
                        <Button
                          size="sm"
                          className="flex-1 gap-1.5"
                          onClick={() => handleDuplicate(template.id)}
                          disabled={duplicatingId === template.id}
                        >
                          {duplicatingId === template.id ? (
                            <>
                              <RiLoader4Line className="h-3.5 w-3.5 animate-spin" />
                              <span className="hidden sm:inline">Duplicating...</span>
                            </>
                          ) : (
                            <>
                              <RiFileCopyLine className="h-3.5 w-3.5" />
                              <span className="hidden sm:inline">Use</span>
                            </>
                          )}
                        </Button>
                      </CardFooter>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      <TemplatePreviewModal
        templateId={previewTemplateId}
        open={previewOpen}
        onClose={handleClosePreview}
        onDuplicate={handleDuplicateFromPreview}
      />
    </SidebarProvider>
  );
}
